import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { notifyAdmins, sendNotification } from '../../utils/notificationService';
import { uploadDocumentToBunny, uploadImageToBunnyDirect } from '../../utils/bunnyUpload';
import DOMPurify from 'dompurify';
import { Upload, Link as LinkIcon, FileText, CheckCircle2, Loader2, Image as ImageIcon, AlertCircle, ExternalLink, X, FileEdit, MessageSquare, ArrowLeft, Medal } from 'lucide-react';
import { useToast } from '../../context/ToastContext';

export default function AssignmentSubmission({ sessionData, courseTitle, courseId, currentUser, onSuccess }) {
  const { addToast } = useToast();
  const [tab, setTab] = useState('images'); // images, pdf, drive
  const [images, setImages] = useState([]); // [{ file, preview }]
  const [pdfFile, setPdfFile] = useState(null);
  const [driveLink, setDriveLink] = useState('');
  const [studentNote, setStudentNote] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    let validFiles = [];
    files.forEach(f => {
      if (f.size > 2 * 1024 * 1024) {
        addToast(`حجم الصورة ${f.name} يتجاوز 2MB`, 'error');
      } else if (!f.type.startsWith('image/')) {
        addToast(`الملف ${f.name} ليس صورة صالحة`, 'error');
      } else {
        validFiles.push({ file: f, preview: URL.createObjectURL(f) });
      }
    });

    if (images.length + validFiles.length > 3) {
      addToast('عذراً، الحد الأقصى 3 صور', 'warning');
      setImages(prev => [...prev, ...validFiles].slice(0, 3));
    } else {
      setImages(prev => [...prev, ...validFiles]);
    }
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      addToast('حجم ملف PDF يتجاوز 10MB', 'error');
      setPdfFile(null);
      return;
    }
    if (file.type !== 'application/pdf') {
      addToast('يجب أن يكون الملف بصيغة PDF فقط.', 'error');
      setPdfFile(null);
      return;
    }
    setPdfFile(file);
  };

  const handleSubmit = async () => {
    if (tab === 'images' && images.length === 0) return addToast('يرجى رفع صورة واحدة على الأقل.', 'warning');
    if (tab === 'pdf' && !pdfFile) return addToast('يرجى اختيار ملف PDF للرفع.', 'warning');
    if (tab === 'drive' && !driveLink) return addToast('يرجى إدخال رابط Drive صحيح.', 'warning');

    setIsSubmitting(true);
    setUploadProgress(10);
    try {
      const uploadedFiles = [];
      if (tab === 'images') {
        let cnt = 0;
        for (const imgObj of images) {
          const url = await uploadImageToBunnyDirect(imgObj.file, 'Assintmint');
          uploadedFiles.push({ name: imgObj.file.name, url });
          cnt++;
          setUploadProgress(10 + Math.round((cnt / images.length) * 40));
        }
      } else if (tab === 'pdf') {
        const url = await uploadDocumentToBunny(pdfFile, (pct) => setUploadProgress(10 + Math.round(pct * 0.4)), 'Assintmint');
        uploadedFiles.push({ name: pdfFile.name, url });
      }

      setUploadProgress(70);

      // Save to Submissions Collection
      const assignmentId = sessionData.id || `u${sessionData.unitIdx}-l${sessionData.lessonIdx}`;
      
      await addDoc(collection(db, 'submissions'), {
        studentId: currentUser.uid,
        studentName: currentUser.displayName || 'طالب',
        courseId: courseId,
        assignmentId: assignmentId,
        assignmentName: sessionData.title,
        assignmentType: sessionData.type || 'assignment',
        files: uploadedFiles,
        driveLink: tab === 'drive' ? driveLink.trim() : null,
        studentNote: studentNote.trim() || null,
        status: 'pending',
        maxScore: sessionData.maxScore || 100,
        submittedAt: serverTimestamp(),
      });

      setUploadProgress(90);

      // Notify Admin
      await notifyAdmins(
        'تم رفع تكليف جديد',
        `قام الطالب ${currentUser.displayName || ''} برفع تكليف: ${sessionData.title}`,
        'info',
        '/admin/assignments'
      );

      // Notify Instructor
      try {
        const courseDoc = await getDoc(doc(db, 'courses', courseId));
        if (courseDoc.exists() && courseDoc.data().instructorId) {
          await sendNotification(
            courseDoc.data().instructorId,
            'تسليم عمـل جديد',
            `قام الطالب ${currentUser.displayName || ''} بتسليم عمل في "${courseTitle}".`,
            'success',
            '/instructor-dashboard/students'
          );
        }
      } catch (err) {
        console.error('Error notifying instructor:', err);
      }

      setUploadProgress(100);
      setShowSuccess(true);
      addToast('تم إرسال تكليفك بنجاح للمراجعة!', 'success');

      // Wait a bit to show success state then close
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 2000);

    } catch (err) {
      console.error(err);
      addToast(err.message || 'فشل في رفع التكليف. تأكد من الاتصال.', 'error');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const safeHtml = DOMPurify.sanitize(sessionData.description || '<p>لا يوجد وصف للتكليف.</p>');

  if (showSuccess) {
    return (
      <div className="w-full max-w-xl mx-auto bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-12 text-center shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[60px] -translate-y-10 translate-x-10"></div>
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', duration: 0.8 }}
          className="relative z-10"
        >
          <div className="w-24 h-24 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl shadow-emerald-500/20">
            <CheckCircle2 className="w-12 h-12" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-4">تم الإرسال بنجاح!</h2>
          <p className="text-slate-500 dark:text-zinc-400 font-bold max-w-sm mx-auto leading-relaxed">
            لقد تم رفع تكليفك بنجاح. سيقوم المدرب بمراجعته وتقييمه في أقرب وقت ممكن.
          </p>
          <div className="mt-10 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 text-emerald-500 font-black text-xs uppercase tracking-widest">
              <Loader2 className="w-4 h-4 animate-spin" />
              جاري تحديث لوحة التحكم...
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      {/* ── Assignment Info Header ── */}
      <div className="bg-white dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-3xl p-6 sm:p-10 relative overflow-hidden shadow-xl">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary-500/5 rounded-full blur-[100px] -translate-y-1/2 pointer-events-none"></div>

        <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center shrink-0 border shadow-lg ${sessionData.type === 'final_project'
              ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-200/50 dark:border-rose-500/20 text-rose-600'
              : 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200/50 dark:border-emerald-500/20 text-emerald-600'
            }`}>
            {sessionData.type === 'final_project' ? <Medal className="w-8 h-8 sm:w-10 sm:h-10" /> : <FileEdit className="w-8 h-8 sm:w-10 sm:h-10" />}
          </div>

          <div className="text-center md:text-right flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-2">
              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${sessionData.type === 'final_project'
                  ? 'bg-rose-500/10 border-rose-500/20 text-rose-500'
                  : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'
                }`}>
                {sessionData.type === 'final_project' ? 'مشروع نهائي' : 'تكليف وحدة'}
              </span>
              <span className="px-3 py-1 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-full text-[10px] font-black text-slate-500 dark:text-zinc-400">
                {courseTitle}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white leading-tight mb-4 tracking-tight">
              {sessionData.title || 'تكليف'}
            </h2>
            <div className="prose prose-slate dark:prose-invert max-w-none prose-p:leading-8 prose-p:text-slate-600 dark:prose-p:text-zinc-400 text-base leading-8 font-medium"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </div>
        </div>
      </div>

      {/* ── Submission Form Area ── */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-2xl">
        <div className="p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 bg-primary-50 dark:bg-primary-500/10 rounded-xl">
              <Upload className="w-6 h-6 text-primary-500" />
            </div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white">مساحة تسليم الإبداع</h3>
          </div>

          {/* Tab Selection Design */}
          <div className="relative bg-slate-100 dark:bg-black/40 p-1.5 rounded-2xl mb-10 flex items-center shadow-inner overflow-x-auto custom-scrollbar no-scrollbar whitespace-nowrap">
            {[
              { id: 'images', label: 'صور (Max 3)', icon: ImageIcon },
              { id: 'pdf', label: 'ملف PDF', icon: FileText },
              { id: 'drive', label: 'رابط خارجي', icon: LinkIcon }
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`relative flex-1 min-w-[120px] sm:min-w-0 py-3.5 px-4 rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all z-10 ${tab === t.id ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300'
                  }`}
              >
                {tab === t.id && (
                  <motion.div
                    layoutId="activeTabBg"
                    className="absolute inset-0 bg-white dark:bg-zinc-800 rounded-xl shadow-md z-[-1] border border-slate-200 dark:border-white/5"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <t.icon className={`w-4 h-4 ${tab === t.id ? 'text-primary-500' : ''}`} />
                {t.label}
              </button>
            ))}
          </div>

          {/* Upload Interactive Panels */}
          <div className="min-h-[200px] mb-10">
            <AnimatePresence mode="wait">
              {tab === 'images' && (
                <motion.div
                  key="images"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <label className="group block relative w-full h-40 border-2 border-dashed border-slate-300 dark:border-white/10 hover:border-primary-500 focus-within:border-primary-500 bg-slate-50 dark:bg-black/20 rounded-2xl cursor-pointer transition-all duration-300 hover:shadow-2xl hover:shadow-primary-500/5">
                    <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageChange} disabled={isSubmitting} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 dark:text-zinc-400 group-hover:scale-105 transition-transform">
                      <div className="w-14 h-14 rounded-full bg-white dark:bg-white/5 flex items-center justify-center mb-3 shadow-md group-hover:text-primary-500 transition-colors">
                        <ImageIcon className="w-7 h-7" />
                      </div>
                      <span className="font-black text-sm sm:text-base">اختر أو اسحب الصور هنا</span>
                      <span className="text-[11px] font-bold mt-1 opacity-60">الحد الأقصى للتسليم الواحد 3 صور</span>
                    </div>
                  </label>

                  {images.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {images.map((img, i) => (
                        <motion.div
                          key={i}
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="relative aspect-square rounded-2xl overflow-hidden shadow-lg border border-slate-200 dark:border-white/10 group bg-slate-100"
                        >
                          <img src={img.preview} alt="preview" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                          <button
                            onClick={() => removeImage(i)}
                            disabled={isSubmitting}
                            className="absolute top-2 right-2 w-8 h-8 bg-rose-500 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity translate-y-[-10px] group-hover:translate-y-0"
                          >
                            <X className="w-5 h-5" />
                          </button>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {tab === 'pdf' && (
                <motion.div
                  key="pdf"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                >
                  <label className="group block relative w-full h-40 border-2 border-dashed border-slate-300 dark:border-white/10 hover:border-primary-500 bg-slate-50 dark:bg-black/20 rounded-2xl cursor-pointer transition-all">
                    <input type="file" className="hidden" accept=".pdf" onChange={handlePdfChange} disabled={isSubmitting} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 dark:text-zinc-400 group-hover:scale-105 transition-transform">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 shadow-md transition-colors ${pdfFile ? 'bg-emerald-500 text-white' : 'bg-white dark:bg-white/5'}`}>
                        {pdfFile ? <CheckCircle2 className="w-7 h-7" /> : <FileText className="w-7 h-7" />}
                      </div>
                      <span className="font-black text-sm sm:text-base">{pdfFile ? pdfFile.name : 'اختر ملف PDF للرفع'}</span>
                      <span className="text-[11px] font-bold mt-1 opacity-60">تنسيق PDF فقط، حجم أقصى 10MB</span>
                    </div>
                  </label>
                </motion.div>
              )}

              {tab === 'drive' && (
                <motion.div
                  key="drive"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <div className="relative group">
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-slate-100 dark:bg-white/5 rounded-xl flex items-center justify-center text-slate-400 group-focus-within:text-primary-500 transition-colors border border-transparent group-focus-within:border-primary-500/30">
                      <LinkIcon className="w-5 h-5" />
                    </div>
                    <input
                      type="url"
                      value={driveLink}
                      onChange={e => setDriveLink(e.target.value)}
                      placeholder="https://drive.google.com/..."
                      disabled={isSubmitting}
                      className="w-full pl-6 pr-16 py-4 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-slate-900 dark:text-white transition-all font-bold placeholder:text-slate-300 dark:placeholder:text-zinc-700"
                      dir="ltr"
                    />
                  </div>
                  <div className="p-4 bg-amber-50 dark:bg-amber-500/5 rounded-2xl border border-amber-100 dark:border-amber-500/10 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-400 font-bold leading-relaxed">
                      يرجى التأكد من أن الرابط "متاح للعرض لأي شخص يمتلك الرابط" حتى نتمكن من تقييم عملك بدقة.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* High-End Note Field */}
          <div className="space-y-3 group mb-10">
            <label className="flex items-center gap-2 text-sm font-black text-slate-700 dark:text-zinc-300 group-focus-within:text-primary-500 transition-colors mr-1">
              <MessageSquare className="w-4 h-4" /> ملاحظة للمدرب (اختياري)
            </label>
            <textarea
              value={studentNote}
              onChange={(e) => setStudentNote(e.target.value)}
              placeholder="اكتب ملاحظتك للمدرب هنا إذا كان لديك أي استفسار أو توضيح إضافي..."
              className="w-full p-5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-slate-900 dark:text-white transition-all font-medium resize-none min-h-[140px] placeholder:text-slate-300 dark:placeholder:text-zinc-700"
              disabled={isSubmitting}
            />
          </div>

          {/* Action Zone */}
          <div className="flex flex-col sm:flex-row items-center gap-4 border-t border-slate-100 dark:border-white/5 pt-8">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full sm:flex-1 relative overflow-hidden bg-primary-600 hover:bg-primary-500 text-white font-black py-6 rounded-2xl shadow-xl shadow-primary-600/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 group/btn"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover/btn:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> جاري إرسال إبداعك... ({uploadProgress}%)</>
              ) : (
                <><Upload className="w-6 h-6" /> إرسال التكليف</>
              )}
            </button>

            {sessionData.isSkippable && !isSubmitting && (
              <button onClick={onSuccess} className="w-full sm:w-auto px-10 py-4.5 rounded-2xl font-black text-slate-500 dark:text-zinc-400 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/10 transition-all flex items-center justify-center gap-2">
                تخي للمرحلة التالية <ArrowLeft className="w-4 h-4 mt-0.5" />
              </button>
            )}
          </div>

          {/* Linear Progress Indicator during upload */}
          {isSubmitting && (
            <div className="w-full h-2 bg-slate-100 dark:bg-black/50 rounded-full overflow-hidden mt-8 shadow-inner">
              <motion.div
                className="h-full bg-gradient-to-r from-primary-600 to-primary-400 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                initial={{ width: 0 }}
                animate={{ width: `${uploadProgress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
